import { createClient } from '@/lib/supabase/client';

export interface RoastSubmission {
  id: string;
  mode: 'text' | 'screenshot';
  roastText?: string;
  screenshotUrl?: string;
  screenshotPath?: string;
  category: string;
  country: string;
  context?: string;
  status: 'pending' | 'approved' | 'rejected';
  submittedAt: string;
  reviewedAt?: string;
  adminNotes?: string;
  votes: number;
  upvotes: number;
  downvotes: number;
}

export interface SubmitRoastPayload {
  mode: 'text' | 'screenshot';
  roastText?: string;
  screenshotFile?: File;
  category: string;
  country: string;
  context?: string;
}

function mapRow(row: any): RoastSubmission {
  return {
    id: row.id,
    mode: row.mode,
    roastText: row.roast_text,
    screenshotUrl: row.screenshot_url,
    screenshotPath: row.screenshot_path,
    category: row.category,
    country: row.country,
    context: row.context,
    status: row.status,
    submittedAt: row.submitted_at,
    reviewedAt: row.reviewed_at,
    adminNotes: row.admin_notes,
    votes: row.votes ?? 0,
    upvotes: row.upvotes ?? 0,
    downvotes: row.downvotes ?? 0,
  };
}

export const submissionService = {
  async submitRoast(payload: SubmitRoastPayload): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();

    let screenshotUrl: string | undefined;
    let screenshotPath: string | undefined;

    // Upload screenshot if provided
    if (payload.mode === 'screenshot' && payload.screenshotFile) {
      const file = payload.screenshotFile;
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `screenshots/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('roast-screenshots')
        .upload(path, file, { cacheControl: '3600', upsert: false });

      if (uploadError) {
        console.error('Screenshot upload error:', uploadError.message, uploadError);
        // Provide a more specific error message based on the error type
        if (uploadError.message?.includes('Bucket not found') || uploadError.message?.includes('bucket')) {
          return { success: false, error: 'Storage bucket not configured. Please contact the admin.' };
        }
        if (uploadError.message?.includes('policy') || uploadError.message?.includes('permission') || uploadError.message?.includes('violates')) {
          return { success: false, error: 'Upload permission denied. Please try again or contact support.' };
        }
        return { success: false, error: `Failed to upload screenshot: ${uploadError.message}` };
      }

      screenshotPath = uploadData.path;
      const { data: urlData } = supabase.storage
        .from('roast-screenshots')
        .getPublicUrl(uploadData.path);
      screenshotUrl = urlData.publicUrl;
    }

    const { error } = await supabase.from('roast_submissions').insert({
      mode: payload.mode,
      roast_text: payload.roastText || null,
      screenshot_url: screenshotUrl || null,
      screenshot_path: screenshotPath || null,
      category: payload.category,
      country: payload.country,
      context: payload.context || null,
      status: 'pending',
    });

    if (error) {
      console.error('Submission insert error:', error.message, error.code, error.details);
      return { success: false, error: `Failed to submit roast: ${error.message}` };
    }

    return { success: true };
  },

  async getAllSubmissions(): Promise<RoastSubmission[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('roast_submissions')
      .select('*')
      .order('submitted_at', { ascending: false });

    if (error) {
      console.error('Fetch submissions error:', error.message);
      return [];
    }

    return (data || []).map(mapRow);
  },

  async updateSubmissionStatus(
    id: string,
    status: 'approved' | 'rejected',
    adminNotes?: string
  ): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    const { error } = await supabase
      .from('roast_submissions')
      .update({
        status,
        admin_notes: adminNotes || null,
        reviewed_at: new Date().toISOString(),
      })
      .eq('id', id);

    if (error) {
      console.error('Update status error:', error.message);
      return { success: false, error: error.message };
    }

    return { success: true };
  },

  async deleteSubmission(id: string): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    const { error } = await supabase.from('roast_submissions').delete().eq('id', id);

    if (error) {
      console.error('Delete submission error:', error.message);
      return { success: false, error: error.message };
    }

    return { success: true };
  },

  async getApprovedSubmissions(): Promise<RoastSubmission[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('roast_submissions')
      .select('*')
      .eq('status', 'approved')
      .order('submitted_at', { ascending: false });

    if (error) {
      console.error('Fetch approved submissions error:', error.message);
      return [];
    }

    return (data || []).map(mapRow);
  },

  async getPublicSubmissions(): Promise<RoastSubmission[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('roast_submissions')
      .select('*')
      .in('status', ['approved', 'pending'])
      .order('submitted_at', { ascending: false });

    if (error) {
      console.error('Fetch public submissions error:', error.message);
      return [];
    }

    return (data || []).map(mapRow);
  },
};
